import {DEFAULT_POINT_COLOR} from '../PanelMediaBrowser/common';
import {cornersToEdges} from './boxCornersToEdges';
import {
  type PointCloudBox,
  type PointCloudPoint,
  type PointCloudPosition,
  type PointCloudRgb,
  type PointCloudScene,
  type PointCloudVector,
} from './types';

/** Last-N truncation budget for card and shared parse (K_card). */
export const MAX_POINTS = 300_000;

type SdkFilePoint =
  | [x: number, y: number, z: number]
  | [x: number, y: number, z: number, categoryOrGreyScale: number]
  | [x: number, y: number, z: number, r: number, g: number, b: number];

type SdkBox = {
  corners: Array<[number, number, number]>;
  label?: string;
  color: [number, number, number];
  score?: number;
};

export type SceneV1 = {
  type: 'lidar/beta';
  points?: SdkFilePoint[];
  vectors?: PointCloudVector[];
  boxes?: SdkBox[];
  center?: [number, number, number];
};

export type Object3DScene = (SdkFilePoint[] & {type: undefined}) | SceneV1;

export type LoadedPointCloudScene = {
  scene: PointCloudScene;
  /** Point count in the file before last-N truncation. */
  sourcePointCount: number;
};

function parseObject3D(fileContents: string): Object3DScene {
  let object3D;
  try {
    object3D = JSON.parse(fileContents);
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'unknown';
    console.log('Received error while parsing JSON:', e);
    throw new Error('Invalid point cloud JSON, parse error was: ' + message);
  }

  if (object3D.type !== 'lidar/beta' && !Array.isArray(object3D)) {
    throw new Error(
      'Unsupported format, must be a list of points or a scene type.'
    );
  }

  return object3D;
}

export function countSourcePoints(object3D: Object3DScene): number {
  const points =
    object3D.type === 'lidar/beta' ? object3D.points || [] : object3D;
  return points.length;
}

function handlePoints(object3D: Object3DScene): PointCloudPoint[] {
  const points =
    object3D.type === 'lidar/beta' ? object3D.points || [] : object3D;

  const truncatedPoints = points.slice(-MAX_POINTS);

  return truncatedPoints.map(point => {
    const [x, y, z, r, g, b] = point;
    const position: PointCloudPosition = [x ?? 0, y ?? 0, z ?? 0];
    const category = r;

    if (r !== undefined && g !== undefined && b !== undefined) {
      return {position, color: [r, g, b] as PointCloudRgb};
    } else if (category && Number.isInteger(category)) {
      return {position, category};
      // oxlint-disable-next-line oxc/double-comparisons -- category && excludes 0, but narrowing this unreachable branch changes point-cloud rendering; it needs a weave owner.
    } else if (category && category >= 0 && category <= 0) {
      return {
        position,
        color: [255 * category, 255 * category, 255 * category],
      };
    } else {
      return {position, color: DEFAULT_POINT_COLOR};
    }
  });
}

function handleBoxes(object3D: Object3DScene): PointCloudBox[] {
  const boxes = (object3D.type === 'lidar/beta' && object3D.boxes) || [];
  return boxes.map(box => {
    const {corners, color, label, score} = box;
    if (corners == null) {
      throw new Error('Box must have corners');
    }
    if (corners.length !== 8) {
      throw new Error('Box must have 8 corners');
    }
    return {
      edges: cornersToEdges(corners),
      label,
      color,
      score,
    };
  });
}

/** Parse Object3D JSON into the shared scene (plain XYZ edges). Truncates once. */
export function loadPointCloudScene(
  fileContents: string
): LoadedPointCloudScene {
  const object3D = parseObject3D(fileContents);
  return {
    scene: {
      points: handlePoints(object3D),
      vectors: (object3D.type === 'lidar/beta' && object3D.vectors) || [],
      boxes: handleBoxes(object3D),
    },
    sourcePointCount: countSourcePoints(object3D),
  };
}
